import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Box, Checkbox, FormControlLabel as MuiFormControlLabel, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Dialog, DialogActions, DialogContent, RenderHTML, Action } from "components";
import { SetStateAction, useEffect, useState } from "react";
import { CourseUnenrollment } from "utils/graphql/CourseUnenrollment";
import { COMPLETE_UNENROLLMENT, GET_COURSE_UNENROLLMENT_DATA } from "utils/queries";
import { useAppDispatch } from "store";
import { emailRegex } from "utils/helpers";
import { useTranslation } from "react-i18next";
import { useFocusStyles } from "hooks/focusBorder";
import { OTHER_REASON } from "utils/constants";
import { useViewport } from "hooks/useViewport";

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontSize: 12,
    color: theme.palette.grey["800"],
  },
}));

const NonEditableField = styled(Box)(({ theme }) => ({
  "& label": {
    display: "block",
    fontSize: "11px",
    color: theme.palette.grey["500"],
  },
  "& span": {
    fontSize: "14px",
    color: theme.palette.grey["800"],
    fontWeight: 700,
  },
  "& button": {
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    position: "relative",
    left: "15px",
    top: "-7px",
  },
  "& p": {
    fontSize: "11px",
    color: theme.palette.grey["500"],
  },
}));

interface CtlWrapperProps {
  isMobile?: boolean;
}

const CtlWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<CtlWrapperProps>(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.grey["A100"]}`,
  width: isMobile ? "200px" : "275px",
  minHeight: "40px",
  marginTop: "5px",
  padding: "0 5px",
  overflow: "visible",
  "&:focus-within": {
    border: "2px solid #000",
  },
  "& input": {
    border: "none",
    width: "100%",
    "&:focus": {
      outline: "none",
    },
  },
  "& select": {
    border: "none",
    width: "100%",
    "&:focus": {
      outline: "none",
    },
  },
}));

const Label = styled("label")(({ theme }) => ({
  display: "block",
  fontSize: "12px",
  color: theme.palette.grey["500"],
  marginTop: "29px",
  marginBottom: "10px",
}));

const TextAreaComponent = styled("textarea")<{ isMobile?: boolean }>(({ isMobile }) => ({
  padding: "5px 5px",
  width: isMobile ? "200px" : "275px",
  fontSize: "12px",
}));

export const UnenrollDialog = ({
  course,
  open,
  handleClose,
  handleEnroll,
  learnerEmail,
  canEditLineManagerEmail,
}: any): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isMobile } = useViewport();
  const [loadData, { data }] = useLazyQuery<CourseUnenrollment>(GET_COURSE_UNENROLLMENT_DATA);
  const [completeUnenrollment] = useMutation(COMPLETE_UNENROLLMENT);

  const dispatch = useAppDispatch();
  const focusClass = useFocusStyles();
  const [allReasons, setAllReasons] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);

  const processUnenrollment = () => {
    const data = {
      courseId: String(course.id),
      lineManagerEmail: managerEmail,
      cancellationReason: reason === OTHER_REASON ? otherReason : reason,
    };
    handleDialogClose();
    dispatch({
      type: "loader/showandhide",
      payload: { show: true, message: t("unenrolling") },
    });
    completeUnenrollment({ variables: { courseUnenroll: data } })
      .then((response: any) => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("unenrolling") },
        });
        if (response?.data?.completeUnenrollment?.hasError) {
          dispatch({
            type: "alert/show",
            payload: {
              type: "error",
              title: t("course_unenrolled_alert_title"),
              message: response?.data?.completeUnenrollment?.messages,
            },
          });
        } else {
          (handleEnroll(),
            dispatch({
              type: "alert/show",
              payload: {
                type: "success",
                title: t("course_unenrolled_alert_title"),
                message: t("course_unenrolled_alert_message"),
              },
            }));
        }
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("unenrolling") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("course_unenrolled_alert_title"),
            message: t("course_unenroll_error_alert_message"),
          },
        });
      });
  };

  useEffect(() => {
    if (!!loadData && !!course && open) {
      loadData({ variables: { courseId: String(course.id) } });
    }
  }, [course, open, loadData]);

  useEffect(() => {
    setAllReasons(
      (data?.unenrollmentData?.cancellationReasons || []).flatMap((reason) =>
        reason ? [reason] : [],
      ),
    );
    setManagerEmail(data?.unenrollmentData?.lineManagerEmail || "");
  }, [data]);

  const handleDialogClose = () => {
    setOtherReason("");
    setReason("");
    setAgreeTerms(false);
    handleClose();
  };

  const disableProcessUnenrollment =
    !agreeTerms ||
    !reason ||
    (reason === OTHER_REASON && !otherReason.trim()) ||
    (canEditLineManagerEmail && (!managerEmail || !new RegExp(emailRegex).test(managerEmail))) ||
    (learnerEmail && learnerEmail.toLowerCase().trim() === managerEmail.toLowerCase().trim());

  const showOtherCancellationTextBox = reason == OTHER_REASON;

  return (
    <Dialog
      id="unenrolldialog"
      onClose={handleDialogClose}
      role="dialog"
      aria-labelledby="unenroll_dialog_label"
      aria-modal="true"
      fullWidth={isMobile}
      open={open}
      minHeight={isMobile ? 0 : 500}
      minWidth={isMobile ? undefined : 600}
      maxHeight={isMobile ? undefined : 800}
      style={{ padding: "0px" }}
      showCloseIcon
      title={t("unenroll")}
    >
      <DialogContent isMobile={isMobile}>
        <Typography
          style={{ fontSize: "16px", color: theme.palette.grey["800"] }}
          component="h2"
          id="unenroll_dialog_label"
        >
          {t("unenroll_from_course")}
        </Typography>
        <Typography
          style={{ fontSize: "12px", fontWeight: 700, color: theme.palette.primary.main }}
          component="h3"
        >
          {course.name}
        </Typography>
        <Box
          style={{
            fontSize: "12px",
            color: theme.palette.grey["800"],
            lineHeight: "16px",
            overflowY: "auto",
            maxHeight: "135px",
          }}
        >
          <RenderHTML content={data?.unenrollmentData?.cancellationPolicy ?? ""} />
        </Box>
        <Box
          display="flex"
          flexDirection={"column"}
          justifyContent="space-between"
          flexWrap={isMobile ? "wrap" : undefined}
          width={isMobile ? "250px" : "600"}
        >
          <div>
            <Label tabIndex={0}>{t("reason_for_cancellation")}</Label>
            <CtlWrapper isMobile={isMobile}>
              <select
                multiple={false}
                value={reason || ""}
                onChange={(event) => setReason(event.target.value)}
                aria-label={t("choose_option_cancellation")}
                aria-required="true"
              >
                <option value="">{t("choose")}</option>
                {allReasons.map((item, idx) => (
                  <option key={idx} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </CtlWrapper>
            {showOtherCancellationTextBox && (
              <>
                <Label htmlFor={t("cancellation_others")}>{t("cancellation_others")}</Label>
                <TextAreaComponent
                  isMobile={isMobile}
                  value={otherReason}
                  aria-label={t("aria_cancellation_others")}
                  aria-required="true"
                  onChange={(event: { target: { value: SetStateAction<string> } }) =>
                    setOtherReason(event.target.value)
                  }
                  rows={5}
                  maxLength={250}
                />
              </>
            )}
          </div>
          <div>
            {function () {
              if (canEditLineManagerEmail) {
                return (
                  <>
                    {/* <Label htmlFor={t('managers_email')}>Manager&apos;s Email</Label> */}
                    <Label htmlFor={t("managers_email")}>{t("managers_email")}</Label>
                    <CtlWrapper isMobile={isMobile}>
                      <input
                        value={managerEmail}
                        aria-label={t("enter_managers_email")}
                        aria-required="true"
                        onChange={(event) => setManagerEmail(event.target.value)}
                      />
                    </CtlWrapper>
                  </>
                );
              }
              return (
                <NonEditableField>
                  {/* <Label>Manager&apos;s Email</Label> */}
                  <Label>{t("managers_email")}</Label>
                  <div style={{ width: 200 }}>{managerEmail}</div>
                </NonEditableField>
              );
            }.call(1)}
          </div>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreeTerms}
              color="primary"
              slotProps={{
                input: { "aria-label": t("agree_terms"), "aria-required": true },
              }}
              onChange={(event) => setAgreeTerms(event.target.checked)}
            />
          }
          tabIndex={0}
          aria-required="true"
          label={t("aria_label_unenroll_confirmation")}
        />
      </DialogContent>
      <DialogActions>
        <Action type="dialog-alt" onClick={handleDialogClose} className={focusClass.focusItem}>
          {t("cancel_and_close")}
        </Action>
        <Action
          type="dialog-primary"
          onClick={processUnenrollment}
          disabled={disableProcessUnenrollment}
          style={{ height: "32px", width: "190px" }}
        >
          {t("confirm_unenrollment")}
        </Action>
      </DialogActions>
    </Dialog>
  );
};
